import * as THREE from "../../libs/three.js/build/three.module.js";

export function addCommas(nStr) {
  nStr += '';
  let x = nStr.split('.');
  let x1 = x[0];
  let x2 = x.length > 1 ? '.' + x[1] : '';
  let rgx = /(\d+)(\d{3})/;
  while (rgx.test(x1)) {
    x1 = x1.replace(rgx, '$1' + ',' + '$2');
  }
  return x1 + x2;
}

export function lineToLineIntersection(P0, P1, P2, P3) {
  const u = P1.clone().sub(P0);
  const v = P3.clone().sub(P2);
  const w0 = P0.clone().sub(P2);

  const a = u.dot(u), b = u.dot(v), c = v.dot(v), d = u.dot(w0), e = v.dot(w0);
  const denom = a * c - b * b;

  let s, t;
  if (Math.abs(denom) < 1e-10) { s = 0; t = (c > 0) ? e / c : 0; }
  else { s = (b * e - c * d) / denom; t = (a * e - b * d) / denom; }

  const Pa = P0.clone().add(u.multiplyScalar(s));
  const Pb = P2.clone().add(v.multiplyScalar(t));
  return Pa.add(Pb).multiplyScalar(0.5);
}

export function getDefaultMeasurementSnapSources() {
  return { pointCloud: true, realityMesh: true, bim: true };
}

/**
 * Scene-frame position → displayed XYZ.
 * `offset` is viewer.measurementDisplayOffset ([dx,dy,dz] added to scene
 * coords). Null/undefined = identity (show scene coords as-is).
 */
export function withMeasurementDisplayOffset(position, offset) {
  const x = position?.x ?? position?.[0];
  const y = position?.y ?? position?.[1];
  const z = position?.z ?? position?.[2];
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) {
    return [x, y, z];
  }
  if (!offset || offset.length < 3) return [x, y, z];
  const dx = Number(offset[0]);
  const dy = Number(offset[1]);
  const dz = Number(offset[2]);
  if (!Number.isFinite(dx) || !Number.isFinite(dy) || !Number.isFinite(dz)) {
    return [x, y, z];
  }
  return [x + dx, y + dy, z + dz];
}

/** Snap sources for navigation (always all, not gated by measurement settings). */
export const NAV_SNAP_SOURCES = { pointCloud: true, realityMesh: true, bim: true };

/** @typedef {'point'|'line'|'surface'} BimSnapMode */

export function getDefaultBimSnapMode() {
  return /** @type {BimSnapMode} */ ('surface');
}

/**
 * Resolve snap toggles from viewer.measurementSnapSources (and optional params override).
 * @param {object} viewer
 * @param {object} [params]
 */
export function getMeasurementSnapSources(viewer, params = {}) {
  const defaults = getDefaultMeasurementSnapSources();
  const fromViewer = viewer?.measurementSnapSources || {};
  const fromParams = params.snapSources || {};
  return {
    pointCloud: fromParams.pointCloud ?? fromViewer.pointCloud ?? defaults.pointCloud,
    realityMesh: fromParams.realityMesh ?? fromViewer.realityMesh ?? defaults.realityMesh,
    bim: fromParams.bim ?? fromViewer.bim ?? defaults.bim,
  };
}

/**
 * BIM geometry snap: point (vertex) / line (edge) / surface (face).
 * @param {object} viewer
 * @param {object} [params]
 * @returns {BimSnapMode}
 */
export function getBimSnapMode(viewer, params = {}) {
  const raw = params.bimSnapMode ?? viewer?.measurementBimSnapMode ?? getDefaultBimSnapMode();
  if (raw === 'point' || raw === 'line' || raw === 'surface') return raw;
  return getDefaultBimSnapMode();
}

/** Apply foreign/npm Matrix4.elements to a Potree Vector3 (dual-THREE safe). */
function applyMatrix4Elements(v, e) {
  if (!e || e.length < 16) return v;
  const x = v.x;
  const y = v.y;
  const z = v.z;
  const w = e[3] * x + e[7] * y + e[11] * z + e[15];
  const invW = Math.abs(w) > 1e-12 ? 1 / w : 1;
  v.x = (e[0] * x + e[4] * y + e[8] * z + e[12]) * invW;
  v.y = (e[1] * x + e[5] * y + e[9] * z + e[13]) * invW;
  v.z = (e[2] * x + e[6] * y + e[10] * z + e[14]) * invW;
  return v;
}

function closestPointOnSegment(p, a, b) {
  const abx = b.x - a.x;
  const aby = b.y - a.y;
  const abz = b.z - a.z;
  const lenSq = abx * abx + aby * aby + abz * abz;
  if (lenSq < 1e-20) return a.clone();
  let t = ((p.x - a.x) * abx + (p.y - a.y) * aby + (p.z - a.z) * abz) / lenSq;
  t = Math.max(0, Math.min(1, t));
  return new THREE.Vector3(a.x + abx * t, a.y + aby * t, a.z + abz * t);
}

/**
 * World-space triangle for a raycast hit (indexed / non-indexed BufferGeometry).
 * @returns {{ a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3 } | null}
 */
function getHitTriangleWorld(hit) {
  const object = hit?.object;
  const geometry = object?.geometry;
  const position = geometry?.attributes?.position;
  if (!position || typeof position.getX !== 'function') return null;

  let ia;
  let ib;
  let ic;
  if (hit.face && hit.face.a != null && hit.face.b != null && hit.face.c != null) {
    ia = hit.face.a;
    ib = hit.face.b;
    ic = hit.face.c;
  } else if (hit.faceIndex != null && geometry.index && typeof geometry.index.getX === 'function') {
    const i = hit.faceIndex * 3;
    ia = geometry.index.getX(i);
    ib = geometry.index.getX(i + 1);
    ic = geometry.index.getX(i + 2);
  } else if (hit.faceIndex != null) {
    const i = hit.faceIndex * 3;
    ia = i;
    ib = i + 1;
    ic = i + 2;
  } else {
    return null;
  }

  const count = position.count ?? 0;
  if (ia < 0 || ib < 0 || ic < 0 || ia >= count || ib >= count || ic >= count) {
    return null;
  }

  if (typeof object.updateMatrixWorld === 'function') {
    try { object.updateMatrixWorld(true); } catch (_) { /* ignore */ }
  }

  const elements = object.matrixWorld?.elements;
  const toWorld = (index) => {
    const v = new THREE.Vector3(position.getX(index), position.getY(index), position.getZ(index));
    return applyMatrix4Elements(v, elements);
  };

  return { a: toWorld(ia), b: toWorld(ib), c: toWorld(ic) };
}

/**
 * Refine a BIM surface hit to vertex / edge / face.
 * @param {THREE.Vector3} surfacePoint
 * @param {{ a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3 } | null} triangle
 * @param {BimSnapMode} mode
 */
function refineBimSnap(surfacePoint, triangle, mode) {
  if (!triangle || mode === 'surface') return surfacePoint.clone();

  if (mode === 'point') {
    const verts = [triangle.a, triangle.b, triangle.c];
    let best = verts[0];
    let bestD = surfacePoint.distanceToSquared(best);
    for (let i = 1; i < verts.length; i++) {
      const d = surfacePoint.distanceToSquared(verts[i]);
      if (d < bestD) {
        bestD = d;
        best = verts[i];
      }
    }
    return best.clone();
  }

  if (mode === 'line') {
    const onEdges = [
      closestPointOnSegment(surfacePoint, triangle.a, triangle.b),
      closestPointOnSegment(surfacePoint, triangle.b, triangle.c),
      closestPointOnSegment(surfacePoint, triangle.c, triangle.a),
    ];
    let best = onEdges[0];
    let bestD = surfacePoint.distanceToSquared(best);
    for (let i = 1; i < onEdges.length; i++) {
      const d = surfacePoint.distanceToSquared(onEdges[i]);
      if (d < bestD) {
        bestD = d;
        best = onEdges[i];
      }
    }
    return best.clone();
  }

  return surfacePoint.clone();
}

function isObjectVisibleInTree(object) {
  let o = object;
  while (o) {
    if (o.visible === false) return false;
    o = o.parent;
  }
  return true;
}

function classifySnapRoot(root) {
  if (root?.userData?.isBimModel) return 'bim';
  if (root?.userData?.isRealityMesh) return 'realityMesh';
  // BIM is registered via addRealityMesh; prefer explicit tags, else treat as mesh.
  return 'realityMesh';
}

/**
 * Collect scene roots eligible for mesh/BIM raycast based on snap toggles.
 * @param {object} viewer
 * @param {{ realityMesh: boolean, bim: boolean }} sources
 * @returns {Array<{ root: object, kind: 'realityMesh'|'bim' }>}
 */
function collectMeshSnapTargets(viewer, sources) {
  const list = viewer?.scene?.realityMeshes;
  if (!list || list.length === 0) return [];

  const out = [];
  for (const root of list) {
    if (!root || root.visible === false) continue;
    const kind = classifySnapRoot(root);
    if (kind === 'bim' && !sources.bim) continue;
    if (kind === 'realityMesh' && !sources.realityMesh) continue;
    out.push({ root, kind });
  }
  return out;
}

/**
 * Raycast duck-typed meshes (Potree THREE vs npm three / Fragments / 3D Tiles).
 * Uses object.raycast when present; ignores instanceof pitfalls of dual THREE.
 * Closest surface hit wins; BIM hits are then refined by bimSnapMode.
 * @param {BimSnapMode} bimSnapMode
 */
function pickMeshIntersection(raycaster, targets, camera, bimSnapMode) {
  let bestHit = null;
  let bestKind = null;
  let bestRoot = null;
  let bestSurfaceDistance = Infinity;

  for (const { root, kind } of targets) {
    const intersects = [];
    try {
      // Prefer root-level API if present (some Fragments wrappers expose it).
      if (typeof root.raycast === 'function' && !root.isObject3D && !root.type) {
        root.raycast(raycaster, intersects);
      } else {
        raycaster.intersectObject(root, true, intersects);
      }
    } catch (_) {
      // Dual-THREE / custom geometry can throw; skip this root.
      continue;
    }

    for (const hit of intersects) {
      if (!hit?.point) continue;
      if (hit.object && !isObjectVisibleInTree(hit.object)) continue;

      const surface = new THREE.Vector3(hit.point.x, hit.point.y, hit.point.z);
      const distance = camera.position.distanceTo(surface);
      if (distance < bestSurfaceDistance) {
        bestSurfaceDistance = distance;
        bestHit = hit;
        bestKind = kind;
        bestRoot = root;
      }
    }
  }

  if (!bestHit || !bestKind) return null;

  const surface = new THREE.Vector3(bestHit.point.x, bestHit.point.y, bestHit.point.z);
  let location = surface.clone();
  let snapFeature = 'surface';
  if (bestKind === 'bim') {
    location = refineBimSnap(surface, getHitTriangleWorld(bestHit), bimSnapMode);
    snapFeature = bimSnapMode;
  }

  return {
    location,
    distance: camera.position.distanceTo(location),
    /** Camera→surface distance before vertex/edge refine (for PC vs mesh depth compare). */
    surfaceDistance: bestSurfaceDistance,
    pointcloud: null,
    point: { position: location.clone() },
    source: bestKind,
    snapFeature,
    object: bestHit.object || bestRoot,
  };
}

/**
 * A point-cloud grab further than this from the cursor is a convenience fallback
 * for sparse data, not something the user actually aimed at.
 */
const NEAR_CURSOR_PX = 6;

/** Depth tie window, expressed in pixels worth of world units at that depth. */
const COPLANAR_TOLERANCE_PX = 4;

/** Floor for the tie window, so extreme close-ups keep a usable tolerance. */
const MIN_COPLANAR_TOLERANCE = 0.01;

/** World-space size of one screen pixel at the given camera distance. */
function worldUnitsPerPixel(camera, distance, screenHeight) {
  const height = Math.max(1, screenHeight);

  if (camera.isOrthographicCamera || camera.type === 'OrthographicCamera') {
    return (camera.top - camera.bottom) / height / (camera.zoom || 1);
  }

  const fov = ((camera.fov || 60) * Math.PI) / 180;
  return (2 * Math.tan(fov / 2) * Math.max(0, distance)) / height;
}

/**
 * Screen-pixel → world ray. Must use THREE.Raycaster.setFromCamera so
 * orthographic views get parallel rays (origin on the camera plane, direction
 * = camera forward). The old "origin = camera.position, unproject z=0.5"
 * construction is perspective-only and makes hover / gizmos / clip-box /
 * panoramas miss in ortho.
 */
export function mouseToRay(mouse, camera, width, height) {
  const ndc = new THREE.Vector2(
    (mouse.x / width) * 2 - 1,
    -(mouse.y / height) * 2 + 1,
  );
  const raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(ndc, camera);
  return raycaster.ray.clone();
}

const _orthoCorner = new THREE.Vector3();
const _orthoLook = new THREE.Vector3();

function isFiniteWorldBox(box) {
  return Boolean(
    box &&
    !box.isEmpty() &&
    Number.isFinite(box.min.x) &&
    Number.isFinite(box.min.y) &&
    Number.isFinite(box.min.z) &&
    Number.isFinite(box.max.x) &&
    Number.isFinite(box.max.y) &&
    Number.isFinite(box.max.z),
  );
}

function viewSpaceZRange(worldBox, invView) {
  let minZ = Infinity;
  let maxZ = -Infinity;
  for (const x of [worldBox.min.x, worldBox.max.x]) {
    for (const y of [worldBox.min.y, worldBox.max.y]) {
      for (const z of [worldBox.min.z, worldBox.max.z]) {
        _orthoCorner.set(x, y, z).applyMatrix4(invView);
        minZ = Math.min(minZ, _orthoCorner.z);
        maxZ = Math.max(maxZ, _orthoCorner.z);
      }
    }
  }
  return { minZ, maxZ };
}

/**
 * Ortho zoom only shrinks left/right/top/bottom. Orbit/Earth zoom still moves
 * the camera toward the pivot (same as perspective), so a close look puts
 * cameraO inside the cloud and the near plane slices it.
 *
 * Slide the camera backward along its look axis until the whole world AABB is
 * in front, then set near/far to that slab. The 2D image does not change —
 * parallel projection ignores distance along the view axis.
 *
 * @param {THREE.OrthographicCamera} camera
 * @param {THREE.Box3} worldBox
 * @param {number} [margin=2]
 */
export function fitOrthographicDepthToBox(camera, worldBox, margin = 2) {
  if (!camera?.isOrthographicCamera || !isFiniteWorldBox(worldBox)) {
    return;
  }

  const slack = Number.isFinite(margin) ? margin : 2;

  camera.updateMatrixWorld(true);
  camera.matrixWorldInverse.copy(camera.matrixWorld).invert();

  let { minZ, maxZ } = viewSpaceZRange(worldBox, camera.matrixWorldInverse);
  const pullBack = maxZ + slack;
  if (pullBack > 0 && Number.isFinite(pullBack)) {
    camera.getWorldDirection(_orthoLook);
    camera.position.addScaledVector(_orthoLook, -pullBack);
    camera.updateMatrixWorld(true);
    camera.matrixWorldInverse.copy(camera.matrixWorld).invert();
    ({ minZ, maxZ } = viewSpaceZRange(worldBox, camera.matrixWorldInverse));
  }

  const near = Math.max(0.1, -maxZ - slack);
  const far = Math.max(near + 10.0, -minZ + slack);
  if (!Number.isFinite(near) || !Number.isFinite(far)) {
    return;
  }

  camera.near = near;
  camera.far = far;
  camera.updateProjectionMatrix();
}

/**
 * Point clouds (GPU pick) + reality-mesh raycast: everything resolvable in the
 * current frame. BIM lives in a second THREE realm behind a Fragments worker,
 * so it is resolved by the two entry points below.
 *
 * The point-cloud grab and the ray-exact hits are kept apart on purpose — see
 * resolveHit for why comparing their raw depths is wrong.
 */
function pickSyncSources(mouse, camera, viewer, pointclouds, params) {

  let renderer = viewer.renderer;

  let nmouse = {
    x: (mouse.x / renderer.domElement.clientWidth) * 2 - 1,
    y: -(mouse.y / renderer.domElement.clientHeight) * 2 + 1
  };

  let pickParams = {};

  if (params.pickClipped) {
    pickParams.pickClipped = params.pickClipped;
  }
  if (params.pickOutsideClipRegion) {
    pickParams.pickOutsideClipRegion = true;
    pickParams.pickClipped = false;
  }

  pickParams.x = mouse.x;
  pickParams.y = renderer.domElement.clientHeight - mouse.y;

  let raycaster = new THREE.Raycaster();
  raycaster.setFromCamera(nmouse, camera);
  let ray = raycaster.ray;

  const sources = getMeasurementSnapSources(viewer, params);

  let pointCloudHit = null;
  let exactHit = null;
  let exactDistance = Infinity;

  if (sources.pointCloud !== false) {
    const clouds = pointclouds || viewer?.scene?.pointclouds || [];
    let closestPointCloud = Infinity;

    for (let pointcloud of clouds) {
      if (!pointcloud || pointcloud.visible === false) continue;

      let point = pointcloud.pick(viewer, camera, ray, pickParams);
      if (!point) continue;

      let distance = camera.position.distanceTo(point.position);
      if (distance < closestPointCloud) {
        closestPointCloud = distance;
        pointCloudHit = {
          location: point.position,
          distance,
          pointcloud,
          point,
          source: 'pointCloud',
          /** How far off the cursor this grab landed, in pixels. */
          pixelOffset: point.pickPixelOffset ?? 0,
        };
      }
    }
  }

  const bimSnapMode = getBimSnapMode(viewer, params);

  // Reality meshes: sync THREE raycast (works for 3D Tiles when geometries load).
  // BIM Fragments: Potree Raycaster cannot hit npm-three meshes — use the async
  // Fragments pick registered by noach-fe (viewer.pickBimSnap).
  if (sources.realityMesh) {
    const meshTargets = collectMeshSnapTargets(viewer, {
      realityMesh: true,
      bim: false,
    });
    if (meshTargets.length > 0) {
      const meshHit = pickMeshIntersection(
        raycaster,
        meshTargets,
        camera,
        bimSnapMode
      );
      ({ hit: exactHit, distance: exactDistance } = closerExact(
        exactHit,
        exactDistance,
        meshHit
      ));
    }
  }

  return { pointCloudHit, exactHit, exactDistance, sources, bimSnapMode };
}

function closerExact(hit, distance, candidate) {
  if (!candidate) return { hit, distance };

  const candidateDistance = candidate.surfaceDistance ?? candidate.distance ?? Infinity;
  return candidateDistance < distance
    ? { hit: candidate, distance: candidateDistance }
    : { hit, distance };
}

/**
 * Decide between the point-cloud grab and the closest ray-exact hit.
 *
 * These two are not measured the same way. PointCloudOctree.pick searches a wide
 * screen-space window and returns the point nearest the cursor within it, which
 * can sit ~32px away, while mesh/BIM hits lie exactly under the cursor. Ranking
 * them by raw camera distance therefore lets an off-cursor grab shadow the
 * surface the user is pointing at: on a grazing ground plane a point a few dozen
 * pixels below the cursor is metres closer, so a BIM building standing on a
 * scanned site becomes impossible to snap to.
 *
 * So a grab only competes on depth when it really is under the cursor, and
 * exact geometry wins near-ties — scan points of a modelled face are noise
 * scattered around that face, half of it in front of the model.
 */
function resolveHit(pointCloudHit, exactHit, exactDistance, camera, viewer) {
  if (!exactHit) return pointCloudHit;
  if (!pointCloudHit) return exactHit;

  if ((pointCloudHit.pixelOffset ?? 0) > NEAR_CURSOR_PX) return exactHit;

  const screenHeight = viewer?.renderer?.domElement?.clientHeight ?? 0;
  const tolerance = Math.max(
    MIN_COPLANAR_TOLERANCE,
    COPLANAR_TOLERANCE_PX * worldUnitsPerPixel(camera, exactDistance, screenHeight)
  );

  return pointCloudHit.distance + tolerance < exactDistance ? pointCloudHit : exactHit;
}

/** Rank the frame's own hits against whatever BIM answer the cache already holds. */
function resolveWithCachedBim(picked, mouse, camera, viewer) {
  const { pointCloudHit, exactHit, exactDistance, sources } = picked;

  if (!sources.bim) {
    return resolveHit(pointCloudHit, exactHit, exactDistance, camera, viewer);
  }

  const withBim = closerExact(
    exactHit,
    exactDistance,
    consumeCachedBimSnap(viewer, mouse, camera)
  );

  return resolveHit(pointCloudHit, withBim.hit, withBim.distance, camera, viewer);
}

/**
 * Unified pick for measurements / annotations / navigation:
 * point clouds (GPU pick) + optional reality-mesh / BIM raycasts.
 *
 * BIM is served from a short-lived cache warmed by a background pick, so it only
 * resolves for callers that poll every frame (tool hover / drag). One-shot
 * callers must use getMousePointCloudIntersectionAsync instead.
 */
export function getMousePointCloudIntersection(mouse, camera, viewer, pointclouds, params = {}) {
  const picked = pickSyncSources(mouse, camera, viewer, pointclouds, params);

  if (picked.sources.bim) {
    scheduleBimSnapPick(viewer, mouse, picked.bimSnapMode);
  }

  return resolveWithCachedBim(picked, mouse, camera, viewer);
}

/**
 * Same pick, but reported twice: the frame's own answer now, and a corrected one
 * once Fragments replies.
 *
 * Pollers get BIM from a cache that a background pick fills a few frames later,
 * which breaks down exactly when it matters — a pointer held still to aim emits
 * no more events, so the last frame stays resolved without BIM. Since the GPU
 * pick has no knowledge of BIM depth, points the model hides stay grabbable, and
 * a marker committed from that frame lands metres behind the surface being aimed
 * at. Waiting for the reply keeps that answer authoritative without stalling the
 * frame, and costs no extra picks — the background one is simply awaited.
 *
 * @param {(intersection: object) => void} onSettled Called only when awaiting BIM
 *   changes the outcome and no newer pick has superseded this one.
 * @returns {object|null} The immediate intersection.
 */
export function getMousePointCloudIntersectionSettling(
  mouse,
  camera,
  viewer,
  pointclouds,
  params = {},
  onSettled
) {
  const picked = pickSyncSources(mouse, camera, viewer, pointclouds, params);
  const immediate = resolveWithCachedBim(picked, mouse, camera, viewer);

  if (!picked.sources.bim) return immediate;

  if (typeof viewer.pickBimSnap !== 'function' || typeof onSettled !== 'function') {
    scheduleBimSnapPick(viewer, mouse, picked.bimSnapMode);
    return immediate;
  }

  const token = (viewer.__bimSnapPickToken = (viewer.__bimSnapPickToken || 0) + 1);
  const mouseCopy = { x: mouse.x, y: mouse.y };

  Promise.resolve()
    .then(() => viewer.pickBimSnap({ mouse: mouseCopy, snapMode: picked.bimSnapMode }))
    .then((hit) => {
      if (token !== viewer.__bimSnapPickToken) return;
      writeBimSnapCache(viewer, mouseCopy, hit, picked.bimSnapMode);

      const bimHit = hit && hit.location
        ? bimHitToIntersection(hit, camera, picked.bimSnapMode)
        : null;
      if (!bimHit) return;

      const withBim = closerExact(picked.exactHit, picked.exactDistance, bimHit);
      const settled = resolveHit(
        picked.pointCloudHit,
        withBim.hit,
        withBim.distance,
        camera,
        viewer
      );

      if (settled && movesResult(settled, immediate)) onSettled(settled);
    })
    .catch(() => {
      /* ignore transient pick errors */
    });

  return immediate;
}

function movesResult(settled, previous) {
  if (!previous) return true;
  if (settled.source !== previous.source) return true;
  return settled.location.distanceTo(previous.location) > 1e-4;
}

/**
 * Same pick, but awaits the Fragments BIM raycast instead of reading the cache.
 * A fire-and-forget pick never lands within the same frame, so one-shot callers
 * (double click, pivot on mouse down) would otherwise always miss BIM.
 */
export async function getMousePointCloudIntersectionAsync(mouse, camera, viewer, pointclouds, params = {}) {
  const { pointCloudHit, exactHit, exactDistance, sources, bimSnapMode } =
    pickSyncSources(mouse, camera, viewer, pointclouds, params);

  if (!sources.bim || typeof viewer.pickBimSnap !== 'function') {
    return resolveHit(pointCloudHit, exactHit, exactDistance, camera, viewer);
  }

  // Supersede any scheduled pick so its older result can't clobber the cache.
  viewer.__bimSnapPickToken = (viewer.__bimSnapPickToken || 0) + 1;

  let hit = null;
  try {
    hit = await viewer.pickBimSnap({
      mouse: { x: mouse.x, y: mouse.y },
      snapMode: bimSnapMode,
    });
  } catch (err) {
    return resolveHit(pointCloudHit, exactHit, exactDistance, camera, viewer);
  }

  // Feed the cache too, so a polling caller right after (scroll zoom) hits warm.
  writeBimSnapCache(viewer, mouse, hit, bimSnapMode);

  const bimHit = hit && hit.location ? bimHitToIntersection(hit, camera, bimSnapMode) : null;
  const withBim = closerExact(exactHit, exactDistance, bimHit);

  return resolveHit(pointCloudHit, withBim.hit, withBim.distance, camera, viewer);
}

function bimHitToIntersection(hit, camera, bimSnapMode) {
  const location = new THREE.Vector3(hit.location.x, hit.location.y, hit.location.z);
  return {
    location,
    distance: camera.position.distanceTo(location),
    surfaceDistance: hit.surfaceDistance ?? hit.distance,
    pointcloud: null,
    point: { position: location.clone() },
    source: 'bim',
    snapFeature: hit.snapFeature || bimSnapMode,
  };
}

/**
 * Fire-and-forget Fragments BIM pick; result lands in viewer.__bimSnapCache.
 * MeasuringTool drag polls sync pick every frame, so the cache warms quickly.
 */
function scheduleBimSnapPick(viewer, mouse, bimSnapMode) {
  if (typeof viewer.pickBimSnap !== 'function') return;
  const token = (viewer.__bimSnapPickToken = (viewer.__bimSnapPickToken || 0) + 1);
  const mouseCopy = { x: mouse.x, y: mouse.y };
  Promise.resolve()
    .then(() =>
      viewer.pickBimSnap({ mouse: mouseCopy, snapMode: bimSnapMode })
    )
    .then((hit) => {
      if (token !== viewer.__bimSnapPickToken) return;
      writeBimSnapCache(viewer, mouseCopy, hit, bimSnapMode);
    })
    .catch(() => {
      /* ignore transient pick errors */
    });
}

function writeBimSnapCache(viewer, mouse, hit, bimSnapMode) {
  const mouseCopy = { x: mouse.x, y: mouse.y };

  if (!hit || !hit.location) {
    viewer.__bimSnapCache = { miss: true, mouse: mouseCopy, t: performance.now() };
    return;
  }

  viewer.__bimSnapCache = {
    location: hit.location,
    distance: hit.distance,
    surfaceDistance: hit.surfaceDistance ?? hit.distance,
    snapFeature: hit.snapFeature || bimSnapMode,
    mouse: mouseCopy,
    t: performance.now(),
  };
}

function consumeCachedBimSnap(viewer, mouse, camera) {
  const c = viewer.__bimSnapCache;
  if (!c || c.miss || !c.location) return null;
  if (performance.now() - c.t > 450) return null;
  if (
    Math.hypot((c.mouse?.x ?? 0) - mouse.x, (c.mouse?.y ?? 0) - mouse.y) > 14
  ) {
    return null;
  }

  return bimHitToIntersection(c, camera, 'surface');
}

export function computeCircleCenter(A, B, C) {
  const AB = B.clone().sub(A);
  const AC = C.clone().sub(A);

  const N = AC.clone().cross(AB).normalize();

  const ab_dir = AB.clone().cross(N).normalize();
  const ac_dir = AC.clone().cross(N).normalize();

  const ab_origin = A.clone().add(B).multiplyScalar(0.5);
  const ac_origin = A.clone().add(C).multiplyScalar(0.5);

  const P0 = ab_origin;
  const P1 = ab_origin.clone().add(ab_dir);

  const P2 = ac_origin;
  const P3 = ac_origin.clone().add(ac_dir);

  const center = lineToLineIntersection(P0, P1, P2, P3);

  return center;
}
